import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import {
  Bug,
  CalendarDays,
  ChartNoAxesColumn,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  FileUp,
  MailPlus,
  RotateCw,
  Kanban,
  LogOut,
  Menu,
  Settings,
  User,
  UsersRound,
  X,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ThemeToggle } from "@/components/ui/theme-toggle"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

const STORAGE_KEY = "main-nav-open"
const DEFAULT_BRAND = "Residential LeadFlow"
const RESIDENTIAL_LIGHT_BRAND_LOGO = "/ResidentialLeadFlow-LightMode.png?v=20260827-residential-2"
const RESIDENTIAL_DARK_BRAND_LOGO = "/ResidentialLeadFlow-DarkMode.png?v=20260827-residential-2"
const RESIDENTIAL_COLLAPSED_LOGO = "/LeadFlowResidential.png?v=20260827-residential-2"

export type NavItemDef = {
  href: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  match: (url: string) => boolean
  adminOnly?: boolean
}

const DEFAULT_NAV_ITEMS: NavItemDef[] = [
  { href: "/customers", icon: UsersRound, label: "Customers", match: (url) => url.startsWith("/customers") || url.startsWith("/leads") },
  { href: "/dashboard", icon: Kanban, label: "Pipeline", match: (url) => url === "/" || url.startsWith("/dashboard") },
  { href: "/reports", icon: ChartNoAxesColumn, label: "Reports", match: (url) => url.startsWith("/reports") },
  { href: "/calendar", icon: CalendarDays, label: "Calendar", match: (url) => url.startsWith("/calendar") },
]

const ADMIN_CHILD_NAV_ITEMS: NavItemDef[] = [
  { href: "/admin/users", icon: User, label: "Users", match: (url) => url.startsWith("/admin/users") },
  { href: "/admin/imports", icon: FileUp, label: "Imports", match: (url) => url.startsWith("/admin/imports") },
  { href: "/admin/invitations", icon: MailPlus, label: "Invitations", match: (url) => url.startsWith("/admin/invitations") },
  { href: "/admin/field-routes-webhooks", icon: Bug, label: "Webhook Logs", match: (url) => url.startsWith("/admin/field-routes-webhooks") },
  { href: "/admin/lifecycle-automation", icon: RotateCw, label: "Lifecycle Automation", match: (url) => url.startsWith("/admin/lifecycle-automation") },
  { href: "/settings", icon: Settings, label: "Settings", match: (url) => url.startsWith("/settings") },
]

function useMainNavOpen() {
  const [open, setOpen] = React.useState<boolean>(true)
  React.useEffect(() => {
    if (typeof window === "undefined") return
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored !== null) setOpen(stored === "true")
  }, [])
  React.useEffect(() => {
    if (typeof window === "undefined") return
    window.localStorage.setItem(STORAGE_KEY, String(open))
  }, [open])
  return [open, setOpen] as const
}

function BrandMark({ compact, brand }: { compact: boolean; brand: string }) {
  if (compact) {
    return <img src={RESIDENTIAL_COLLAPSED_LOGO} alt={`${brand} logo`} className="block h-8 w-8 shrink-0 object-contain object-center" loading="eager" decoding="async" />
  }

  return (
    <span className="relative block h-10 w-auto max-w-[12rem] shrink-0">
      <img src={RESIDENTIAL_LIGHT_BRAND_LOGO} alt={brand} className="block h-10 w-auto max-w-[12rem] shrink-0 object-contain dark:hidden" loading="eager" decoding="async" />
      <img src={RESIDENTIAL_DARK_BRAND_LOGO} alt={brand} className="hidden h-10 w-auto max-w-[12rem] shrink-0 object-contain dark:block" loading="eager" decoding="async" />
    </span>
  )
}

export function MainNav({ items = DEFAULT_NAV_ITEMS, brandHref = "/dashboard" }: { items?: NavItemDef[]; brandHref?: string } = {}) {
  const { props, url } = usePage<PageProps>()
  const isAdmin = props.current_user?.admin ?? false
  const brand = props.branding?.app_name ?? DEFAULT_BRAND
  const visibleItems = items
  const [open, setOpen] = useMainNavOpen()
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [adminExpanded, setAdminExpanded] = React.useState(false)

  return (
    <>
      <aside className={cn("sticky top-0 hidden h-screen shrink-0 flex-col border-r border-hairline bg-page transition-[width] duration-200 lg:flex", open ? "w-56" : "w-14")}>
        <RailBody open={open} onToggle={() => setOpen(!open)} items={visibleItems} brandHref={brandHref} brand={brand} isAdmin={isAdmin} adminExpanded={adminExpanded} onToggleAdmin={() => setAdminExpanded((value) => !value)} url={url} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-ink-display/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} aria-hidden />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-page shadow-xl">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-hairline px-4">
              <span className="font-display text-sm font-semibold text-ink-display">{brand}</span>
              <button type="button" onClick={() => setMobileOpen(false)} className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display" aria-label="Close navigation"><X className="h-4 w-4" /></button>
            </div>
            <RailNav open items={visibleItems} onClose={() => setMobileOpen(false)} isAdmin={isAdmin} adminExpanded={adminExpanded} onToggleAdmin={() => setAdminExpanded((value) => !value)} url={url} />
            <div className="border-t border-hairline p-2"><UserMenu open /></div>
          </aside>
        </div>
      )}

      <button type="button" onClick={() => setMobileOpen(true)} className="fixed right-3 top-3 z-30 inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-hairline bg-page text-ink-body hover:bg-surface lg:hidden" aria-label="Open navigation"><Menu className="h-4 w-4" /></button>
    </>
  )
}

function RailBody({ open, onToggle, items, brandHref, brand, isAdmin, adminExpanded, onToggleAdmin, url }: { open: boolean; onToggle: () => void; items: NavItemDef[]; brandHref: string; brand: string; isAdmin: boolean; adminExpanded: boolean; onToggleAdmin: () => void; url: string }) {
  return (
    <>
      <div className={cn("flex h-14 shrink-0 items-center gap-3 border-b border-hairline px-3", open ? "justify-between" : "justify-center")}>
        {open ? (
          <Link href={brandHref} className="flex min-w-0 items-center gap-2 text-ink-display no-underline" aria-label={brand}>
            <BrandMark compact={false} brand={brand} />
            <span className="sr-only">{brand}</span>
          </Link>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            className="flex min-w-0 items-center gap-2 text-ink-display no-underline"
            aria-label="Expand navigation"
          >
            <BrandMark compact brand={brand} />
          </button>
        )}
        {open && <button type="button" onClick={onToggle} className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display" aria-label="Collapse sidebar"><ChevronsLeft className="h-4 w-4" /></button>}
      </div>

      <RailNav open={open} items={items} isAdmin={isAdmin} adminExpanded={adminExpanded} onToggleAdmin={onToggleAdmin} url={url} />

      <div className="border-t border-hairline p-2"><UserMenu open={open} /></div>
    </>
  )
}

function RailNav({ open, items, isAdmin, adminExpanded, onToggleAdmin, onClose, url }: { open: boolean; items: NavItemDef[]; isAdmin: boolean; adminExpanded: boolean; onToggleAdmin: () => void; onClose?: () => void; url: string }) {
  const adminActive = url.startsWith("/admin")
  return (
    <nav className={cn("flex min-h-0 flex-1 flex-col gap-1 p-2 text-sm", open ? "overflow-x-hidden overflow-y-auto" : "overflow-visible")}>
      {items.map((item) => item.adminOnly && !isAdmin ? null : <NavItem key={item.href} href={item.href} icon={item.icon} label={item.label} active={item.match(url)} open={open} onClick={onClose} />)}
      {isAdmin && <AdminSection open={open} active={adminActive} expanded={adminExpanded} onToggleExpanded={onToggleAdmin} onClose={onClose} url={url} />}
    </nav>
  )
}

function AdminSection({ open, active, expanded, onToggleExpanded, onClose, url }: { open: boolean; active: boolean; expanded: boolean; onToggleExpanded: () => void; onClose?: () => void; url: string }) {
  return (
    <div className="group/nav-item relative mt-2">
      <button type="button" onClick={onToggleExpanded} className={cn("flex items-center gap-3 rounded-md no-underline", open ? "px-3 py-2" : "mx-auto h-9 w-9 justify-center px-0 py-0", active ? "bg-accent-faded text-accent-display" : "text-ink-body hover:bg-surface hover:text-ink-display")} aria-expanded={expanded} aria-label={open ? `${expanded ? "Collapse" : "Expand"} Admin` : "Admin"}>
        <Bug className="h-4 w-4 shrink-0" />
        {open && <span className="truncate">Admin</span>}
        {open && <span className="ml-auto inline-flex h-4 w-4 items-center justify-center">{expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>}
      </button>
      {!open && (
        <>
          <span role="tooltip" className="pointer-events-none absolute left-full top-1/2 z-50 ml-[13px] -translate-y-1/2 whitespace-nowrap rounded-md border border-hairline bg-page px-2 py-1 text-xs font-medium text-ink-display opacity-0 shadow-sm transition-opacity group-hover/nav-item:opacity-100">Admin</span>
          {expanded && <div className="absolute left-full top-0 z-50 ml-2 w-44 rounded-lg border border-hairline bg-page p-2 shadow-xl">{ADMIN_CHILD_NAV_ITEMS.map((item) => <NavItem key={item.href} href={item.href} icon={item.icon} label={item.label} active={item.match(url)} open onClick={onClose} />)}</div>}
        </>
      )}
      {open && expanded && <div className="ml-4 mt-1 flex flex-col gap-1 border-l border-hairline pl-2">{ADMIN_CHILD_NAV_ITEMS.map((item) => <NavItem key={item.href} href={item.href} icon={item.icon} label={item.label} active={item.match(url)} open={open} onClick={onClose} />)}</div>}
    </div>
  )
}

function NavItem({ href, icon: Icon, label, active, open, onClick }: { href: string; icon: React.ComponentType<{ className?: string }>; label: string; active?: boolean; open: boolean; onClick?: () => void }) {
  return (
    <div className="group/nav-item relative">
      <Link href={href} onClick={onClick} className={cn("flex items-center gap-3 rounded-md no-underline transition-colors", open ? "px-3 py-2" : "mx-auto h-9 w-9 justify-center", active ? "bg-accent-faded text-accent-display" : "text-ink-body hover:bg-surface hover:text-ink-display")}>
        <Icon className="h-4 w-4 shrink-0" />
        {open && <span className="truncate">{label}</span>}
      </Link>
      {!open && <span role="tooltip" className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md border border-hairline bg-page px-2 py-1 text-xs font-medium text-ink-display opacity-0 shadow-md transition-opacity group-hover/nav-item:opacity-100">{label}</span>}
    </div>
  )
}

function UserMenu({ open }: { open: boolean }) {
  const { props } = usePage<PageProps>()
  const email = props.current_user?.email ?? "user@example.com"
  const initial = email.charAt(0).toUpperCase()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={open ? undefined : email} className={cn("group/user relative flex w-full items-center gap-3 rounded-md text-left text-ink-body hover:bg-surface hover:text-ink-display focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent", open ? "px-2 py-2" : "h-10 justify-center")}>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-faded text-xs font-semibold text-accent">{initial}</span>
          {open ? <span className="min-w-0 flex-1 truncate text-sm">{email}</span> : <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md border border-hairline bg-page px-2 py-1 text-xs font-medium text-ink-display opacity-0 shadow-md transition-opacity group-hover/user:opacity-100">{email}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuLabel className="normal-case tracking-normal">
          <span className="block text-[10px] uppercase tracking-wider text-ink-muted">Signed in as</span>
          <span className="block truncate text-xs font-medium text-ink-display">{email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => router.visit("/profile")}><User /> Profile</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => router.visit("/profile/password")}><Settings /> Password</DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-2"><ThemeToggle block /></div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => router.delete("/logout")}><LogOut /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
